import { AlertCircle, X } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useNotificationStore } from "@/features/notifications/utils/notification-store";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import { AnnouncementType } from "@classview/shared-types";

import { useGetActiveAnnouncements } from "../api/get-active-announcements";

export function AnnouncementWarning() {
  const { organisation } = useOrganisation();
  const dismissAnnouncement = useNotificationStore(
    (state) => state.dismissAnnouncement,
  );
  const dismissedAnnouncements = useNotificationStore(
    (state) => state.dismissedAnnouncements,
  );

  const { data: activeAnnouncements, isSuccess } = useGetActiveAnnouncements(
    organisation.id,
    {
      select: (data) =>
        data.filter(
          (announcement) =>
            announcement.type === AnnouncementType.ANNOUNCEMENT &&
            !dismissedAnnouncements.includes(announcement.id),
        ),
    },
  );

  const hasActiveAnnouncements = isSuccess && activeAnnouncements.length > 0;

  if (!hasActiveAnnouncements) return null;

  const handleDismiss = (announcementId: string) => {
    dismissAnnouncement(announcementId);
  };

  return (
    <div className="mb-6">
      {activeAnnouncements.map((announcement) => (
        <Alert key={announcement.id} className="[&>svg]:text-amber-600">
          <AlertCircle className="size-6 text-inherit" aria-hidden="true" />

          <AlertTitle className="text-secondary">
            {announcement.title}
          </AlertTitle>
          <AlertDescription className="text-muted">
            <div className="flex flex-col">
              <p className="mb-2">{announcement.message}</p>
              <Button
                variant="ghost"
                className="self-start"
                type="button"
                onClick={() => handleDismiss(announcement.id)}
              >
                Dismiss
              </Button>
            </div>
          </AlertDescription>
          <Button
            variant="ghost"
            onClick={() => handleDismiss(announcement.id)}
            className="absolute right-4 top-4 rounded-sm !p-2 text-card-foreground opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
          >
            <X className="size-4" />
            <span className="sr-only">Dismiss</span>
          </Button>
        </Alert>
      ))}
    </div>
  );
}
